// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { useDraftTrips } from '../hooks/useDraftTrips';

type Props = {
  currentTripId: string;
};

const SidebarMenu = ({ currentTripId }: Props): JSX.Element => {
  const { theme } = useAITTheme();
  const history = useHistory();
  const [historyOpen, setHistoryOpen] = useState(false);
  const { drafts, loading } = useDraftTrips(currentTripId);
  const styles = useMemo(() => getStyles(theme), [theme]);

  const isNewPage = currentTripId === 'new';
  const isActive = (tripId: string) => currentTripId === tripId;

  const handleNavigateToNew = () => {
    history.push('/trips/ai_messages/new');
  };

  const handleNavigateToTrip = (tripId: string) => {
    history.push(`/trips/${tripId}/ai_messages`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' });
  };

  const truncateMessage = (message: string, maxLength = 10) => {
    if (!message) return '(メッセージなし)';
    if (message.length <= maxLength) return message;
    return `${message.slice(0, maxLength)}...`;
  };

  return (
    <div style={styles.container}>
      <button type="button" style={isNewPage ? styles.itemActive : styles.item} onClick={handleNavigateToNew}>
        新規チャット
      </button>
      <button type="button" style={styles.accordionHeader} onClick={() => setHistoryOpen(!historyOpen)}>
        <span>最近の履歴</span>
        <span style={styles.accordionIcon}>{historyOpen ? '▼' : '▶'}</span>
      </button>
      {historyOpen && (
        <div style={styles.accordionContent}>
          {loading ? (
            <div style={styles.statusText}>読み込み中...</div>
          ) : drafts.length === 0 ? (
            <div style={styles.statusText}>履歴がありません</div>
          ) : (
            drafts.map(draft => (
              <button
                key={draft.tripId}
                type="button"
                style={isActive(draft.tripId) ? styles.historyItemActive : styles.historyItem}
                onClick={() => handleNavigateToTrip(draft.tripId)}
              >
                <div style={styles.historyMessage}>{truncateMessage(draft.lastMessage)}</div>
                <div style={styles.historyDate}>{formatDate(draft.createdAt)}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const buttonReset = {
  background: 'none',
  border: 'none',
  textAlign: 'left' as const,
  display: 'block' as const,
  boxSizing: 'border-box' as const,
  font: 'inherit',
  outline: 'none',
  padding: 0,
  margin: 0
};

const getStyles = (theme: AITTheme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: theme.contentBgColor,
    borderRight: `1px solid ${theme.grayBorderColor}`,
    minWidth: '200px',
    height: '100%',
    overflowY: 'auto' as const
  },
  item: {
    ...buttonReset,
    padding: '16px 24px',
    cursor: 'pointer',
    fontSize: '14px',
    color: theme.textColor
  },
  itemActive: {
    ...buttonReset,
    padding: '16px 24px',
    marginTop: '16px',
    cursor: 'pointer',
    fontSize: '14px',
    color: theme.textColor,
    backgroundColor: '#e6f4fb',
    fontWeight: 600 as const,
    borderLeft: `4px solid ${theme.linkColor}`
  },
  accordionHeader: {
    ...buttonReset,
    padding: '16px 24px',
    cursor: 'pointer',
    fontSize: '14px',
    color: theme.textColor,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  accordionIcon: {
    fontSize: '10px',
    color: theme.grayTextColor
  },
  accordionContent: {
    backgroundColor: theme.whiteColor
  },
  historyItem: {
    ...buttonReset,
    padding: '12px 24px',
    cursor: 'pointer',
    fontSize: '13px',
    borderBottom: `1px solid ${theme.grayBorderColor}`
  },
  historyItemActive: {
    ...buttonReset,
    padding: '12px 24px',
    cursor: 'pointer',
    fontSize: '13px',
    borderBottom: `1px solid ${theme.grayBorderColor}`,
    backgroundColor: '#e6f4fb',
    borderLeft: `4px solid ${theme.linkColor}`
  },
  historyMessage: {
    color: theme.textColor,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  historyDate: {
    fontSize: '11px',
    color: theme.grayTextColor,
    marginTop: '4px'
  },
  statusText: {
    padding: '12px 24px',
    fontSize: '13px',
    color: theme.grayTextColor
  }
});

export default SidebarMenu;
